import React, { useState } from 'react'
import {
  Heading,
  Text,
  Button,
  TextInput,
  FormControl,
  InlineLink
} from '@primer/react-brand'
import CenteredAuthLayout from '../components/CenteredAuthLayout'
import { validateRequired, isValidEmailFormat } from '../utilities/validateAuthForm'

// Page 1 of the password-reset flow: request a reset link.
// Posts to Devise's PasswordsController#create (JSON) and, on success, swaps the
// form for a paranoid "Check your email" confirmation that never reveals whether
// the address is registered.
const ForgotPasswordPage = ({ signInPath = '/sign-in', membershipPath, currentUser }) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    setEmail(e.target.value)
    if (error) setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const requiredError = validateRequired([
      { value: email, message: 'Email is required.' }
    ])
    if (requiredError) { setError(requiredError); return }

    if (!isValidEmailFormat(email)) {
      setError({ type: 'invalid_format', message: 'Enter a valid email address' })
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/users/password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        credentials: 'same-origin',
        body: JSON.stringify({ user: { email: email.trim() } })
      })

      const data = await response.json()

      if (data.success) {
        setSubmitted(true)
      } else {
        setError({ type: 'general', message: 'Something went wrong. Please try again.' })
      }
    } catch (err) {
      setError({ type: 'general', message: 'Something went wrong. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const ctaStyle = {
    marginTop: '18px',
    backgroundColor: 'var(--base-color-scale-blue-5)',
    borderColor: 'var(--base-color-scale-blue-5)',
    borderRadius: '6px'
  }

  if (submitted) {
    return (
      <CenteredAuthLayout membershipPath={membershipPath} signInPath={signInPath} currentUser={currentUser}>
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '22px',
            marginLeft: 'auto',
            marginRight: 'auto',
            backgroundColor: 'var(--base-color-scale-blue-0)'
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z" stroke="var(--base-color-scale-blue-5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <Heading as="h1" size="5">Check your email</Heading>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
          If your email address exists in our database, you'll receive a password recovery link at your email address in a few minutes.
        </Text>
        <div
          style={{
            marginTop: '22px',
            padding: '12px 14px',
            backgroundColor: 'var(--base-color-scale-gray-0)',
            border: '1px solid var(--base-color-scale-gray-1)',
            borderRadius: '8px'
          }}
        >
          <Text size="200" variant="muted" as="span">
            Didn't get it? Check your spam folder, or{' '}
            <InlineLink href="#" onClick={(e) => { e.preventDefault(); setSubmitted(false) }}>try again</InlineLink>{' '}
            with a different address.
          </Text>
        </div>
        <Text size="200" variant="muted" as="div" style={{ marginTop: '22px' }}>
          <InlineLink href={signInPath}>Return to sign in</InlineLink>
        </Text>
      </CenteredAuthLayout>
    )
  }

  return (
    <CenteredAuthLayout membershipPath={membershipPath} signInPath={signInPath} currentUser={currentUser}>
      <Heading as="h1" size="5">Reset your password?</Heading>
      <Text size="200" variant="muted" as="div" style={{ marginTop: '14px' }}>
        If you wish to reset your password, enter the email address used to log in to your account here, and submit. You'll get an email with a link to reset your password.
      </Text>

      <form onSubmit={handleSubmit} style={{ marginTop: '28px' }}>
        <FormControl fullWidth validationStatus={error ? 'error' : undefined}>
          <FormControl.Label>Your Email Address</FormControl.Label>
          <TextInput
            name="email"
            type="email"
            value={email}
            onChange={handleChange}
            placeholder="Email address"
            autoFocus
          />
          {error && (
            <FormControl.Validation validationStatus="error">{error.message}</FormControl.Validation>
          )}
        </FormControl>

        <Button
          type="submit"
          variant="primary"
          block={true}
          disabled={loading}
          style={ctaStyle}
        >
          {loading ? 'Sending...' : 'Email me password reset instructions'}
        </Button>

        <Text size="200" variant="muted" as="div" align="center" style={{ marginTop: '24px' }}>
          Remembered it?{' '}
          <InlineLink href={signInPath}>Back to sign in</InlineLink>
        </Text>
      </form>
    </CenteredAuthLayout>
  )
}

export default ForgotPasswordPage
